/**
 * Contracts of the document instances back-end (base path /api/v2/document-archive-docs).
 *
 * A row of this collection is a document a property ACTUALLY owns, child of a
 * `documentarchives` row through `documentArchiveId`. It is the instance level of the
 * dossier: the type level lives in the document template (@see DocumentTemplateDoc).
 *
 * It is NOT a mirror of the legacy `flatDocuments`: the two collections are separate and
 * evolve independently. Code written for one must not be pointed at the other.
 *
 * Three consequences that drive the whole front-end design:
 *  - a row does NOT carry the document code: `key`, `topic`, `subTopic`, `area` and
 *    `docTitle` do not exist here. The type is referenced by `documentTemplateDocId`,
 *    and everything describing the type is read through that reference;
 *  - there is no global listing: `documentArchiveId` is mandatory on the list endpoint,
 *    because the owning company is two hops away (on the property) and could not be
 *    scoped per tenant in a single query;
 *  - `deleted` is a soft flag flipped by DELETE and PATCH .../restore, never writable.
 *
 * @since 3.0.0
 */

import { DocumentArchiveSearchLabel } from "src/app/shared/interfaces/document-archive-search.interface";
import {
  DocumentTemplateArea,
  DocumentTemplateContent,
  DocumentTemplateDoc,
  DocumentTemplateTranslation,
} from "src/app/shared/interfaces/document-template.interface";

/**
 * One entry of the PDF generation log of a document.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPdfMsg {
  _id?: string;
  msg: string;
  sent: boolean;
}

/**
 * A document owned by a property, as returned by the list and by GET /:docId.
 *
 * The field set is stable on what is active here, but NOT closed: the back-end model
 * still carries commented blocks of the legacy shape, so treat additions as expected.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDoc {
  _id: string;
  /** Parent archive. Not writable: moving a row across archives would move it across properties. */
  documentArchiveId: string;
  /** Derived from the archive by the back-end, never read from the request. */
  propertyId: string;
  /**
   * Reference to the level 3 row of the template (@see DocumentTemplateDoc), replacement
   * of the legacy `key` string. NOT populated: it is the ObjectId as a string.
   *
   * This is the ONLY link to the document type: index the template tree by `_id` and
   * look the type up here. There is no `?key=` filter, and the row carries no code.
   */
  documentTemplateDocId: string;
  /** Condo structure element this document belongs to, when the section is repeated per element */
  structureObjectId?: string;
  expired: boolean;
  /** ISO 8601 */
  dateExpired?: string;
  mandatory: boolean;
  contract: boolean;
  privacy: boolean;
  /**
   * Read only: flipped by DELETE / PATCH restore.
   *
   * ⚠️ A `true` here DOES reach the tree, in one case only: the row belonged to a mapping element
   * that was removed, so it was deleted BY THE CASCADE and not by anybody's hand. It then arrives
   * inside the `active: false` block of that element, which is hidden until the user asks for the
   * deleted structures. Rows deleted by hand never arrive.
   *
   * Such a row is read `mandatory: false` — the element is gone, its documents are not obligations —
   * so it lands in the `optionalPresent` band (or `expired`, which wins), never among the unfulfilled
   * ones. It counts in the counters of its own block and in no other.
   *
   * WHAT MUST NOT BE OFFERED ON IT: edit, delete (the bulk endpoint would answer `not_found`) and,
   * for now, the download — the gate that serves the files excludes deleted rows and answers 404.
   * Reading the archived values is fine: that costs no call.
   */
  deleted: boolean;
  /** Set only by a manual user deletion, never by the automatic sweeps */
  toHardDelete: boolean;
  /** Anchor of the retention window of the hard delete cron, not derivable from updatedAt */
  hardDeleteDate?: string;
  /** Writable by platform roles only */
  mockup: boolean;
  /**
   * Free form data entry values. The keys are the `fields[].name` of the data type bound
   * to the document type (@see DataTypeField), so they are identifiers of the values
   * already archived, not labels.
   */
  data: { [key: string]: any };
  pdfmsg?: DocumentArchiveDocPdfMsg[];
  cancellationRequestDate?: string;
  cancellationRequestBy?: any;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: any;
  updatedBy?: any;
}

/**
 * A document of the listing.
 *
 * Same row as @see DocumentArchiveDoc plus `present`, which the back-end computes and only
 * the LISTING carries: `GET /:docId`, POST, PUT, DELETE and restore answer the stored row,
 * without it.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocListItem extends DocumentArchiveDoc {
  /**
   * True when the document has actually been uploaded, false when the row is only the
   * obligation coming from the template, still to be fulfilled.
   *
   * NEVER recompute this. The rule is not "the row exists", and it is not "`data` is
   * filled" either: a half filled form without a barcode is NOT present. The back-end
   * applies the same rule here and in `counters`, so reading the field is the only way to
   * stay consistent with the numbers shown next to the list.
   *
   * One exception, the back-end's own: a VIRTUAL document (`mockup`, NEW-M44) never carries a
   * barcode, and it is present as soon as it carries a form — still outside the counters.
   */
  present: boolean;
  /**
   * The code shown in the "Codice" column, computed by the back-end out of `data.barCode`:
   * six digits, a space, the type letter (e.g. "000019 D").
   *
   * Always in the response, `null` when the barcode does not match the expected shape — and
   * `null` is then the answer, not a reason to fall back on deriving something from
   * `data.barCode`: the front-end used to do exactly that, in five duplicated copies and two
   * variants that disagreed with each other.
   *
   * NOT an identifier: it is a fragment of the barcode, it is not unique across properties,
   * and it must never be used as a join key or to compare documents. Use `_id`.
   *
   * A document can be `present: true` with `documentCode: null` — never read presence out of
   * this field.
   */
  documentCode: string | null;
  /**
   * The document type of the row, named: `_id`, `title` and `translation`, the label of the search
   * rows. A RETIRED type is named too; a type that no longer exists comes as
   * `{ _id: "", title: "", translation: null }`, never `null` (MAP-A1, back-end 0e242629).
   *
   * Only the listing (`GET /document-archive-docs`) carries it, and only from that back-end on: the
   * tree and the other answers do not, hence optional here.
   */
  documentType?: DocumentArchiveSearchLabel;
}

/**
 * A row as it arrives INSIDE THE TREE.
 *
 * Same row as @see DocumentArchiveDocListItem plus the four fields the back-end resolves for the
 * contracts section (@see DocumentArchiveTreeContent): that section gathers every `contract: true`
 * row archived anywhere in the archive, and those four fields are what its table needs to be
 * printable without going back to `data`.
 *
 * ⚠️ ALL FOUR ARE ABSENT EVERYWHERE ELSE, THE SAME ROW INCLUDED: a contract rendered under its own
 * area does NOT carry them, because computing them for the whole archive would be paid on every
 * read of the tree. So test their PRESENCE, never their truthiness — an empty `executorName` and a
 * `null` `executorSupplierId` are the back-end answering "nobody", and must not be turned into a
 * reason to fall back on `data`.
 *
 * One exception, on `structureLabel` only: the four sanitary network types (legacy ND1, ND2, ND25,
 * ND26) carry it in their own section too, without the other three fields. So a borrowed copy of
 * the contracts section is recognised by `executorName`, never by `structureLabel`.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeRow extends DocumentArchiveDocListItem {
  /**
   * Label to print in brackets next to the document type: the name of the structure element, name
   * and surname for an employee, the POD for an electricity supply contract, the contract number
   * for a medium voltage one. On the four sanitary network types (legacy ND1, ND2, ND25, ND26),
   * which hang from no element, the names of the active water meters declaring the "Idrico
   * sanitario" usage, the ones that make the property owe those documents: name only, comma
   * separated, oldest meter first.
   *
   * EMPTY when the row hangs from no element (a general contract) or when the element no longer
   * exists, and on the four sanitary types when no meter qualifies: print nothing, not a
   * placeholder.
   *
   * Sent on the rows of the contracts section and, for the four sanitary types only, on their rows
   * in their own section too, where it is always a string, possibly empty.
   */
  structureLabel?: string;
  /**
   * `false` ONLY when the element exists and has been switched off — the row is then shown
   * greyed out and struck through, as the legacy dossier shows it.
   *
   * `true` also for a row hanging from no element: there is nothing switched off in a general
   * contract. So the greying test is `=== false`, and absence is not "inactive".
   */
  structureActive?: boolean;
  /**
   * The "Fornitore" column, ALREADY RESOLVED: the current name of the registered supplier when the
   * row names one, otherwise the first non empty value of the legacy chain of `data` fields.
   * Empty string when neither exists.
   *
   * Never recompose it from `data`: the chain is twelve fields in a precise order, and
   * `data.ragioneSocialeFornitore*` is a snapshot of the name taken when the row was archived,
   * stored as an ObjectId on most rows and as a string on the others.
   */
  executorName?: string;
  /**
   * Id of the registered supplier, always as a string, or `null`.
   *
   * `null` means the document names an executor who is NOT in the suppliers registry: there is no
   * supplier page to open, and it is an answer rather than a missing value.
   */
  executorSupplierId?: string | null;
}

/**
 * Counts of the whole filtered set, computed by the back-end.
 *
 * They ignore the pagination — same values on every page — and follow the filters, so they
 * describe exactly the set `totalCount` describes.
 *
 * They count ROWS, not distinct barcodes: the same document uploaded twice counts twice.
 *
 * Guaranteed identities:
 *   mandatoryPresent + mandatoryMissing + optionalPresent === totalCount
 *   totalPresent === mandatoryPresent + optionalPresent
 *
 * There is deliberately NO "non mandatory missing" count: those rows are outside the set
 * (@see DocumentArchiveDocList). Deriving one as a remainder of `totalCount` would always
 * yield zero and would read as "nothing optional is missing", which is not what it means.
 *
 * ⚠️ THESE ARE NOT THE BANDS, despite three shared names
 * (@see DocumentArchiveSortGroup): a mandatory document that is uploaded but EXPIRED counts in
 * `mandatoryPresent` AND sits in the `expired` band. The counters say whether the obligation is
 * covered; the bands say where the row goes on screen. So the numbers next to a band heading
 * would not match the rows under it — do not wire one to the other.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocCounters {
  /** Mandatory documents that are present */
  mandatoryPresent: number;
  /** Mandatory documents that are NOT present, and still inside `items` */
  mandatoryMissing: number;
  /** Non mandatory documents that are present, the only non mandatory ones returned */
  optionalPresent: number;
  /** Present documents, mandatory and not */
  totalPresent: number;
}

/**
 * Payload accepted by PUT /document-archive-docs/:docId. Every key is optional.
 *
 * `documentArchiveId` and `propertyId` are deliberately absent: the back-end rejects them,
 * because moving a row between archives would move it between properties, potentially between
 * companies.
 *
 * `mockup` is writable by platform roles only, so it is not offered here.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocUpdatePayload {
  documentTemplateDocId?: string;
  structureObjectId?: string;
  mandatory?: boolean;
  contract?: boolean;
  privacy?: boolean;
  expired?: boolean;
  /** ISO 8601, `null` accepted to clear it */
  dateExpired?: string | null;
  /**
   * The data entry values. Keys are the `fields[].name` of the data type: keys prefixed with
   * `$`, containing a dot, or named `__proto__` / `constructor` / `prototype` are rejected at
   * any depth.
   *
   * `barCode`, `document` and `documentFile` (plus `files`, `numFiles` and `code` on a paper
   * document) belong to the server: the back-end IGNORES them here and keeps the stored values.
   */
  data?: { [key: string]: any };
}

/**
 * Payload of POST /document-archive-docs/update-docs: the save of the "Modifica dati aggiuntivi"
 * dialog, for EVERY save it makes — the form, the files, or both, on one or more rows.
 *
 * Every named row must belong to `documentArchiveId` (the AUTHORISATION BOUNDARY, as on the bulk
 * delete) and carry the SAME `data.barCode`: they are the rows of one archived document. The rows
 * must not be deleted, mockups or obligations still to be filled (no barcode). At least one of
 * `data`, `addFiles` and `removeFiles` is required, otherwise 400.
 *
 * NOT ATOMIC ACROSS ROWS: the answer is a report of what reached each row
 * (@see DocumentArchiveDocUpdateDocsResult). A refusal (4xx) writes nothing.
 *
 * It moves NO billing: no charge line on the balance, no flat-rate counter.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocUpdateDocsPayload {
  documentArchiveId: string;
  /** The rows the save is written on, 1 to `ARCHIVE_UPDATE_MAX_DOCS` */
  docIds: string[];
  /**
   * The form, COMPLETE, as the PUT used to receive it: the back-end writes it on every named row
   * and REMOVES the keys it no longer holds, so a field the user emptied and the front-end does not
   * send is deleted on purpose.
   *
   * Must NOT carry `barCode`, `document` and `documentFile`, nor `files`, `numFiles` and `code` on a
   * paper document: they belong to the server and are ignored, and a long file list inside `data`
   * can break the 300 keys limit of the form (400).
   *
   * Refused (400) when the named rows use different data forms (`dataTypeId`).
   */
  data?: { [key: string]: any };
  /**
   * Uploads to attach, as `POST /api/v2/files` returned them: ids or `{ _id }` objects, up to
   * `ARCHIVE_UPDATE_MAX_ADD_FILES`. Only the caller's own uploads are accepted. Each one is copied
   * ONCE and appended to every named row, in this order.
   *
   * Refused (400) on a paper document, and when the same file is also in `removeFiles`.
   */
  addFiles?: string[];
  /**
   * Storage names (`filename` of the entries of `data.document[]`) to detach from every named row
   * holding them, up to `ARCHIVE_UPDATE_MAX_REMOVE_FILES`. Never by position, never by path.
   *
   * Nothing is deleted from disk. Removing the last file leaves the document filed, with all its
   * data and no file: it does NOT go back to a red obligation.
   *
   * Refused (400) on a paper document.
   */
  removeFiles?: string[];
}

/**
 * Why update-docs left a file operation out on one row, inside a 200.
 *
 *  - `already_attached`: the row already held that file — a repeated call. Ignorable;
 *  - `not_attached`: the row did not hold the file to detach. Ignorable;
 *  - `changed_meanwhile`: the row changed between the checks and the write (deleted, or the same
 *    file added by another request). The user is told and the tree is reloaded.
 *
 * Typed as a string too: an unknown reason must be readable, not a type error.
 *
 * @since 3.0.0
 */
export type DocumentArchiveDocUpdateDocsSkipReason =
  | "already_attached"
  | "not_attached"
  | "changed_meanwhile"
  | string;

/**
 * One file operation update-docs did not perform on one row.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocUpdateDocsSkip {
  docId: string;
  /** `filename` of the entry, or the upload id of an added file */
  file?: string;
  reason: DocumentArchiveDocUpdateDocsSkipReason;
}

/**
 * Report of POST /document-archive-docs/update-docs.
 *
 * Read, not just acknowledged: the edit is not atomic across rows. Nothing in it is enough to patch
 * the dossier in place, so a successful save is followed by a reload of the tree.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocUpdateDocsResult {
  documentArchiveId: string;
  barCode: string;
  /** The rows written */
  updated: string[];
  /** Uploads copied into the property's directory: one per added file, whatever the rows */
  filesCopied: number;
  /** File entries appended, counted per row */
  entriesAdded: number;
  /** File entries detached, counted per row */
  entriesRemoved: number;
  skipped: DocumentArchiveDocUpdateDocsSkip[];
}

/** Most rows one update-docs call accepts: a longer list is a 400 */
export const ARCHIVE_UPDATE_MAX_DOCS = 100;
/** Most uploads one update-docs call attaches: a longer list is a 400 */
export const ARCHIVE_UPDATE_MAX_ADD_FILES = 20;
/** Most files one update-docs call detaches: a longer list is a 400 */
export const ARCHIVE_UPDATE_MAX_REMOVE_FILES = 100;

/**
 * Keys of `data` that belong to the server on every row: update-docs and the PUT ignore them, and
 * the front-end does not send them. `createdBy` and `createdAt` record who filed the document and
 * when: the back-end stamps them at the insert and keeps the stored ones on every update.
 *
 * @since 3.0.0
 */
export const ARCHIVE_SERVER_OWNED_DATA_KEYS = [
  "barCode",
  "document",
  "documentFile",
  "createdBy",
  "createdAt",
];

/**
 * Keys of `data` that ALSO belong to the server on a paper document: the parts and the folder code
 * the delivery note and the scanning are built on.
 *
 * @since 3.0.0
 */
export const ARCHIVE_PAPER_SERVER_OWNED_DATA_KEYS = ["files", "numFiles", "code"];

/**
 * One document type to archive, inside an insert.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocInsertSelection {
  /**
   * The document TYPE, i.e. the `_id` of a level 3 node of the tree — the entries of
   * `groups[].documents`. It replaces the legacy `key`, and `key` / `title` / `subDoc` are not sent
   * any more.
   */
  documentTemplateDocId: string;
  /**
   * The mapping elements this type is being archived for: the `key` of the blocks with
   * `source: "structureObject"`, and ONLY the ones the user selected.
   *
   * Ids alone — the legacy `{ id, value, type, name }` objects are gone, because the row stores a
   * reference and the back-end resolves the name. Omit it, or send it empty, for a type that hangs
   * from no element.
   */
  structureObjectIds?: string[];
}

/**
 * Payload of POST /document-archive-docs/insert-multi-docs, the insert of the 3.0.0 archive.
 *
 * ONE call archives ONE document: the same `data` and the same files are written to every row it
 * produces, and every row carries the same `barCode`. Naming 2 types × 3 elements writes 6 rows —
 * that is the shape the legacy has too, not a mistake.
 *
 * Two calls in total, as before: `POST /files` first (unchanged), this one after.
 *
 * WHAT THE BACK-END DOES AROUND THE WRITE (verified on its code on 2026-10-28):
 *  - it computes `dateExpired` for every row it writes, per document type and element;
 *  - it then re-runs the rules that act on OTHER documents, so an insert CAN make another
 *    obligation appear or disappear elsewhere in the tree: reload the whole tree, never patch it;
 *  - in the same pass it expires the previous copy of the most-recent-only types (IB10, IA7, AC11
 *    and AJ1), as the legacy does;
 *  - it grows `property.totalDocsInserted` and `totalDocsProgressive` by one, and updates the four
 *    archive-wide lists: conductors, interventions, supplier names (AH1-AH16) and years of storage
 *    (AA11, AA12, EXT-27).
 *
 * ⚠️ BILLING AND THE SUPPLIER COUNTER WAIT FOR A SWITCH of the back-end
 * (`NEW_ARCHIVE_BILLING_ENABLED`), OFF until the cut-over: until then NOTHING of that moves — no
 * charge on the balance, no flat rate allowance, no supplier counter. Once on:
 *  - for an insert by an admin or an operator, a charge on the open balance and, on a flat rate past
 *    its first balance, the allowances: one document and EVERY PDF;
 *  - for any role, the document count of the supplier the form names.
 *
 * LIMITS enforced by the back-end, all of them a 400: 50 types, 100 elements per type, 20 files.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocInsertPayload {
  /** In the BODY, not in the path: same id the legacy passed as `:documentId` */
  documentArchiveId: string;
  /**
   * The data entry values, keys being the `fields[].name` of the data type
   * (@see DocumentArchiveDocUpdatePayload.data for the rejected key shapes).
   */
  data?: { [key: string]: any };
  docsSelected: DocumentArchiveDocInsertSelection[];
  /**
   * The uploads, as `POST /files` answered. Both shapes are accepted — the ids, or the objects
   * carrying `_id` — so what the upload returned can be forwarded without remapping it.
   *
   * A temp upload is swept after 15 days: a form left open that long answers 410, and the files have
   * to be uploaded again. Nothing is archived in that case — every file is checked before the first
   * one is copied, so the request can simply be repeated.
   */
  documentFile?: Array<string | { _id: string }>;
  /**
   * The row to fill (EXT-21): the `_id` of a row of this archive still waiting for its document,
   * sent by the «+» of that row while the insert still files on its type and its element. Without
   * it the back-end fills only a row holding no form at all. @see isFillRowSelected
   */
  fillDocId?: string;
}

/**
 * Report of the insert.
 *
 * `created` are new rows, `filled` are rows that already existed as an uncovered obligation and are
 * now filled. Concatenate them for "what did I just archive"; keep them apart when comparing the
 * screen before and after.
 *
 * Nothing here is enough to update the dossier in place: numbering, counters and grouping are
 * composed by the back-end, so a successful insert is followed by a reload of the tree.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocInsertResult {
  /** Shared by every row the call produced */
  barCode: string;
  documentArchiveId: string;
  propertyId: string;
  created: string[];
  filled: string[];
  /** How many files were archived */
  files: number;
}

/**
 * Shape the back-end enforces on the barcode of a paper insert: "S", exactly six digits, "C".
 *
 * ⚠️ NOT anchored at the start, on purpose: the PREFIX IS FREE, and both the shapes present in the
 * real data are accepted (`<propertyId>S000037C` and the older numeric `004887004727S000025C`). Test
 * it on the UPPERCASED string — the back-end normalizes before matching, and so must the front-end,
 * or a lowercase label would be refused here and accepted there.
 *
 * The trailing letter is what tells a paper document from a digital one for the rest of its life
 * (`documentCode` ends with "C" or with "D"), which is why the back-end validates it instead of
 * storing whatever arrives — the legacy checked nothing and left 3.834 duplicated paper barcodes
 * behind.
 *
 * @since 3.0.0
 */
export const PAPER_BARCODE_PATTERN = /S\d{6}C$/;

/** Maximum length the back-end accepts on the barcode */
export const PAPER_BARCODE_MAX_LENGTH = 200;

/**
 * Whether ONE PART of the document has been digitised yet.
 *
 * Per part and not per document: the parts of the same document are scanned separately and are
 * routinely in different states — two done, four still to do.
 *
 * `"missing"` is what puts the part in the checklist of the scanning team, and it is the only value
 * a front-end can legitimately send at insert time: the bytes do not exist yet. An absent or unknown
 * value is coerced to `"missing"` by the back-end.
 *
 * @since 3.0.0
 */
export type DocumentArchiveDocPaperFileStatus = "missing" | "uploaded";

/**
 * Sheet count of ONE part, by paper format, written BY THE SCANNING STEP.
 *
 * ⚠️ NOT an input of the insert: it says how many A0…A4 sheets came out of digitising that part, so
 * it cannot exist before the part has been scanned. The endpoint accepts it only for compatibility
 * with the legacy payload. Send nothing.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPaperScan {
  /** "A0" | "A1" | "A2" | "A3" | "A4" */
  type?: string;
  qty?: number;
}

/**
 * One PART of a paper document — not a folder, and not the document either.
 *
 * A paper document arrives as one physical bundle and is split into the parts it will be SCANNED
 * into, each with its own printed QR label: the technical dossier of a building is a report, some
 * drawings, the calculations. `status` and the scan counts are per part, because each part is
 * digitised on its own.
 *
 * `name` IS the label, and it carries the type of the part in its own spelling.
 * @see buildPaperPartName for the convention, which the back-end does NOT enforce: on `name` it only
 * checks that it is a non empty string of at most 200 characters, and uppercases it.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPaperFile {
  /**
   * UPPERCASED by the back-end. The historical rows were not, so a lookup by name must compare
   * CASE INSENSITIVELY: 31.964 of them hold the barcode in lowercase while `code` holds it in upper.
   */
  name: string;
  status?: DocumentArchiveDocPaperFileStatus;
  /** Written by the scan, never sent at insert @see DocumentArchiveDocPaperScan */
  scan?: DocumentArchiveDocPaperScan[];
}

/**
 * The five kinds of part, and the letter each one contributes to the name of its label.
 *
 * A CLOSED vocabulary, and the letters are data: they are spelled inside the names of 8.342 rows
 * already archived and read back by whoever scans them, so they are not renamable.
 *
 * @since 3.0.0
 */
export const PAPER_PART_TYPES: Array<{ letter: string; labelKey: string }> = [
  { letter: "R", labelKey: "properties.documentsV3.insertDialog.paper.types.R" },
  { letter: "T", labelKey: "properties.documentsV3.insertDialog.paper.types.T" },
  { letter: "C", labelKey: "properties.documentsV3.insertDialog.paper.types.C" },
  { letter: "S", labelKey: "properties.documentsV3.insertDialog.paper.types.S" },
  { letter: "V", labelKey: "properties.documentsV3.insertDialog.paper.types.V" },
];

/**
 * Name of the label of one part, i.e. what gets printed on its QR sticker.
 *
 * TWO SHAPES, and which one applies depends on how many parts the document has IN TOTAL:
 *  - a document in ONE part carries the bare barcode, with no suffix and therefore no type. That is
 *    the historical convention and it is the majority of the archive (31.964 rows);
 *  - a document in TWO OR MORE carries `<barCode>_<LETTER><index>`, where the index is GLOBAL over
 *    the document, 1-based and CONTINUOUS ACROSS THE TYPES — two reports and four drawings are
 *    `_R1 _R2 _T3 _T4 _T5 _T6`, not `_R1 _R2 _T1 _T2 _T3 _T4`. Types can be mixed freely.
 *
 * So adding a second part RENAMES the first one, and removing one renumbers everything after it: the
 * name is a function of the whole list and must be recomputed, never stored and edited.
 *
 * The back-end neither generates nor validates this — it only uppercases — so getting it wrong is
 * silent, and shows up as a QR label the scanning team cannot place.
 *
 * @since 3.0.0
 *
 * @param barCode    The barcode of the document, already uppercased
 * @param letter     Type of THIS part @see PAPER_PART_TYPES
 * @param index      Position of this part in the document, 0-based
 * @param totalParts How many parts the document has
 */
export function buildPaperPartName(
  barCode: string,
  letter: string,
  index: number,
  totalParts: number
): string {
  if (totalParts <= 1) {
    return barCode;
  }
  return `${barCode}_${letter}${index + 1}`.toUpperCase();
}

/**
 * The form of a paper insert: the free data entry values PLUS the five keys the back-end demands.
 *
 * Every other key is stored as it arrives but still VALIDATED — nothing is dropped in silence, so an
 * unacceptable one is a 400 and not a missing value discovered later. The limits are: 6 levels of
 * nesting, 300 keys in the whole object, 5000 characters per string, no key starting with `$` or
 * containing a dot, and never `__proto__` / `constructor` / `prototype`.
 *
 * `barCode` and `document` are the SERVER'S: the first is overwritten with the normalized `code`,
 * the second is removed. They are absent here so that no screen believes it is writing them.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPaperInsertData {
  /**
   * The barcode PRINTED ON THE FOLDER LABEL the operator is holding, hence sent by the front-end and
   * not generated by the back-end. @see PAPER_BARCODE_PATTERN
   */
  code: string;
  /**
   * How many PDFs this paper document will be split into, and therefore how many parts it declares:
   * the back-end REJECTS a value that does not equal `files.length` with a 400.
   *
   * ⚠️ DERIVE IT, never let it be typed. It is what the invoicing cron bills — one base PDF plus
   * `numFiles - 1` supplementary ones — so a number that disagrees with the list describes PDFs that
   * are charged for and that nobody can ever scan. The back-end refuses instead of correcting it,
   * because silently rewriting it would hide a front-end that lost count of its own parts.
   *
   * `0` with an empty `files` is legitimate: a document declaring no parts.
   */
  numFiles: number;
  /** The parts, 0 to 20 of them, and `numFiles` must equal how many there are */
  files?: DocumentArchiveDocPaperFile[];
  /**
   * Required, non empty, up to 200 characters: the NUMERIC code of the legacy area the document lands
   * in — "1" administrative, "2" contracts, "3" technical, "4" property units.
   *
   * NOT the identifier of the taxonomy (`"tecnico"`), even though the two describe the same four
   * areas: this value is stored as it arrives and read back by the DELIVERY NOTE, whose 34.176
   * existing entries are numeric. The back-end validates the length and nothing else, so a name
   * travels unnoticed onto the note the scanning team works from, where nothing can decode it.
   *
   * The mapping lives in `PAPER_AREA_CODES` of the dossier page, and the legacy dossier applies the
   * same one in `DocumentTypeStructure.getAreaFromChild2`.
   */
  area: string;
  /** Required, non empty, up to 5000 characters. It becomes the `content` of the delivery note. */
  contenutoDocumento: string;
  /** The rest of the data entry form */
  [key: string]: any;
}

/**
 * Payload of POST /document-archive-docs/insert-multi-paper-docs: a PHYSICAL delivery.
 *
 * The paper twin of @see DocumentArchiveDocInsertPayload, and the replacement of the legacy
 * `PATCH /document-archives/:documentId/insert-multi-paper-docs`.
 *
 * ⚠️ THERE IS NO `documentFile`, and no `POST /files` before it: nothing has been scanned yet. The
 * rows are archived without attachments and the PDFs are attached later, by the scanning team, which
 * finds them through the barcode.
 *
 * It also opens the entry on the DELIVERY NOTE of the day (`ddt*` of the answer), which is that
 * team's work list. That is why it is back-office only: `canCreateDocuments` is not enough, the role
 * has to be of type `admin` or `operator`, and a customer role holding the permission gets a 403.
 *
 * AROUND THE WRITE it does what the digital insert does — expiry, the rules on other documents,
 * expiry of the previous copy, the four archive-wide lists — and the supplier counter behind the same
 * back-end switch, off until the cut-over (verified on the back-end code on 2026-10-28). What it
 * never does is bill: no charge on the balance and no flat rate allowance, whatever the switch,
 * because at delivery time there is no PDF to charge for. `property.totalDocsInserted` and
 * `totalDocsProgressive` do grow by one each, and they are NOT in the answer any more: re-read the
 * property if a screen shows them.
 *
 * ⚠️ "Never bills" is about THIS call only. The delivery note is billed later by a cron, which
 * charges one base PDF plus `numFiles - 1` supplementary ones — so `numFiles` IS money, just not yet.
 * @see DocumentArchiveDocPaperInsertData.numFiles
 *
 * EVERY CHECK RUNS BEFORE THE FIRST WRITE: a 400 / 403 / 404 / 409 means nothing was written at all,
 * so the form can stay open for the correction.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPaperInsertPayload {
  /** In the BODY, not in the path: same id the legacy passed as `:documentId` */
  documentArchiveId: string;
  /**
   * 1 to 50 document types. ⚠️ DEDUPLICATE THEM: the same `documentTemplateDocId` sent twice writes
   * two rows, the back-end does not collapse them (it does collapse duplicated
   * `structureObjectIds`).
   */
  docsSelected: DocumentArchiveDocInsertSelection[];
  data: DocumentArchiveDocPaperInsertData;
  /** The row to fill, as on the digital insert. @see DocumentArchiveDocInsertPayload.fillDocId */
  fillDocId?: string;
}

/**
 * Report of a paper insert.
 *
 * Same `created` / `filled` split as the digital one, and the same conclusion: it cannot patch the
 * dossier in place, so a success is followed by a reload of the tree.
 *
 * ⚠️ NO `files` COUNT, unlike the digital report: nothing was copied, and a `0` would read as
 * "processed, and there were none".
 *
 * The delivery note is SHARED by every paper delivery of the same day: `ddtId` and `ddtCode` (the
 * date, e.g. "20260818") repeat across inserts, while `ddtDocumentId` is new every time.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocPaperInsertResult {
  /** Uppercased, and the same string on the archive rows and on the delivery note entry */
  barCode: string;
  documentArchiveId: string;
  propertyId: string;
  /** Ids of the rows the archive did not expect */
  created: string[];
  /** Ids of the obligations the archive was already showing as "to be uploaded" */
  filled: string[];
  ddtId: string;
  /** Code of the delivery note, which is its date */
  ddtCode: string;
  ddtDocumentId: string;
}

/**
 * Payload of POST /document-archive-docs/insert-multi-virtual-docs (NEW-M44): the VIRTUAL documents,
 * the rows the legacy calls «virtuali» and stores with `mockup: true` — a form with no file and no
 * code, which admin and operator file to show an archive filled in (FE-M5).
 *
 * A CLOSED BODY, read by zod: a field more is a 400, inside every entry of `docsSelected` too. So no
 * `documentFile`, and no `fillDocId`: a virtual document never fills the row waiting for its
 * document, it is always a NEW row, and the waiting one stays beside it.
 *
 * ONE NEW ROW PER TYPE AND ELEMENT, all with the same `data`; a type naming no element gives one row
 * on none. Every row is virtual, with no code, the deadline of its own type and element, and
 * `mandatory` from its type (false on the free plans). The back-end updates the dropdown lists of
 * the archive and the document count of the supplier; nothing is billed, no delivery note opens.
 *
 * ⚠️ NOT IDEMPOTENT: the same request sent twice writes its rows twice, so whatever sends it stays
 * off while it is out.
 *
 * Admin and operator only: any other role gets a 403 before the body is read. Every check runs before
 * the first write, so an error means nothing was written.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocVirtualInsertPayload {
  /** In the BODY, as on the other inserts */
  documentArchiveId: string;
  /**
   * The form, an object. The back-end requires no field of it: the page asks for the supplier and the
   * date, as the legacy page does. Its `barCode`, `document`, `documentFile`, `createdBy` and
   * `createdAt` are ignored.
   */
  data: { [key: string]: unknown };
  /** 1 to 50 document types, each naming 0 to 100 elements */
  docsSelected: DocumentArchiveDocInsertSelection[];
}

/**
 * Report of the creation of virtual documents, answered `200` and not `201` as the other inserts.
 *
 * No `barCode`, which a virtual document never has, and no `filled`, since it never fills a row. As
 * after every insert, the dossier is composed by the back-end: the tree is read again to show them.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocVirtualInsertResult {
  documentArchiveId: string;
  propertyId: string;
  /** Ids of the rows written, one per type and element, in the order of the request */
  created: string[];
}

/**
 * One document the bulk delete did NOT touch.
 *
 * `not_found` covers both a non existent id and an id belonging to ANOTHER archive: the back-end
 * does not tell them apart on purpose. The realistic case is a row somebody else deleted while
 * this screen was open — reloading the tree makes it disappear.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocBulkSkip {
  docId: string;
  reason: string;
}

/**
 * Report of DELETE /document-archive-docs/bulk.
 *
 * The operation is NOT atomic, so this is a report and not an acknowledgement: it has to be read,
 * because part of the list can succeed while the rest is skipped.
 *
 * `deleted` holds the ids that are deleted AFTER the call, the ones that already were included.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocBulkDeleteResult {
  deleted: string[];
  skipped: DocumentArchiveDocBulkSkip[];
  counts: {
    requested: number;
    deleted: number;
    skipped: number;
  };
}

/**
 * Limits the back-end puts on the id list of the two contract actions. Both ends are a 400.
 *
 * Enforced by the front-end too, so that the 400 is unreachable: an empty selection is a dialog
 * that should not have offered a confirm button, and a selection over the ceiling is a dialog that
 * should have said so before spending the call.
 *
 * @since 3.0.0
 */
export const CONTRACT_ACTION_MIN_DOCS = 1;
/** @see CONTRACT_ACTION_MIN_DOCS */
export const CONTRACT_ACTION_MAX_DOCS = 200;

/**
 * The communication that goes with a cancellation.
 *
 * ⚠️ `cancellationType` IS THE SWITCH OF THE WHOLE ACTION, not a label on it: `"immediate"` expires
 * the contracts there and then and reopens the empty obligation behind them, while ANY OTHER VALUE
 * only records the communication and leaves the expiry date alone. Same semantics as the legacy
 * `PATCH /document-archives/:id/cancel-contract`. There is no third behaviour, so the front-end
 * sends one of the two documented values and never a third string that would read as a mode of its
 * own.
 *
 * `subject` and `body` are MANDATORY and must not be empty — unlike the legacy dialog, which let the
 * subject through empty and substituted a default one at send time.
 *
 * The three recipient lists are optional and take either `{ email }` objects or plain strings. The
 * objects are sent here because that is what the phonebook widget already produces.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveContractCancellationMessage {
  subject: string;
  body: string;
  recipients?: Array<string | { email: string }>;
  recipientsCc?: Array<string | { email: string }>;
  recipientsBcc?: Array<string | { email: string }>;
  /** `"immediate"` expires the contracts now; `""` only records the communication */
  cancellationType: string;
}

/**
 * Payload of POST /document-archive-docs/cancel-contract.
 *
 * `documentArchiveId` is the AUTHORISATION BOUNDARY, exactly as on the bulk delete: an id belonging
 * to another archive is not touched and comes back among the skipped ones.
 *
 * What the back-end does on top of the expiry: it renders the message to a PDF and ATTACHES it to
 * the rows — added to their history, not replacing what is filed — stamps who asked and when
 * (@see DocumentArchiveDoc.cancellationRequestDate) and switches off any automatic renewal.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveContractCancelPayload {
  documentArchiveId: string;
  /** 1 to 200 ids, of the 3.0.0 collections — NOT the legacy `flatDocuments` ones */
  documentArchiveDocIds: string[];
  message: DocumentArchiveContractCancellationMessage;
}

/**
 * Payload of POST /document-archive-docs/expire-contract.
 *
 * No message: this one only moves the expiry date, so there is nothing to communicate and nothing
 * to attach.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveContractExpirePayload {
  documentArchiveId: string;
  /** 1 to 200 ids @see DocumentArchiveContractCancelPayload.documentArchiveDocIds */
  documentArchiveDocIds: string[];
}

/**
 * Report of both contract actions.
 *
 * A REPORT AND NOT AN ACKNOWLEDGEMENT, and the distinction matters more here than on the bulk
 * delete: an id the back-end does not recognise DOES NOT FAIL THE CALL. The answer is a 200 with
 * `skipped` counted and the reason listed, so a caller that only looks at the HTTP status reports a
 * complete success over rows that were never touched.
 *
 * Three answers to tell apart:
 *  - `skipped > 0`: partial. A warning, not a blocking error, followed by a reload of the tree;
 *  - `updated === 0`: nothing was recorded at all, whatever the HTTP status says;
 *  - otherwise: done.
 *
 * RELOAD THE TREE afterwards in every case: an immediate cancellation and an expiry can open a new
 * mandatory row that was not on the page before (@see placeholders).
 *
 * @since 3.0.0
 */
export interface DocumentArchiveContractActionResult {
  /** Rows the action was recorded on */
  updated: number;
  /** Rows actually expired — 0 on a cancellation that only records the communication */
  expired: number;
  /** Empty obligations reopened behind the expired contracts */
  placeholders: number;
  /** Ids the back-end left alone. NOT an error: read it, warn, reload */
  skipped: number;
  /**
   * One entry per skipped id, with its motive.
   *
   * Deliberately untyped: the shape of an entry is not pinned by the contract, and inventing one
   * here would render "[object Object]" in front of an operator the day it changes. The count above
   * is what the dialogs report.
   */
  reasons: any[];
  /** Where the PDF of the message was filed. Cancellation only — absent on an expiry */
  pdfPath?: string;
}

/**
 * The document types `PATCH /document-archive-docs/:docId/disable` accepts, by `title`.
 *
 * The endpoint answers 400 on anything else, so this list is what keeps the action off the rows it
 * would only fail on. It is the BACK-END rule mirrored here, not a front-end preference: it moves
 * when the endpoint moves, and nothing else is to be added to it locally.
 *
 * `title` AND NOT `key`: `key` is retired on the 3.0.0 dossier and is not to be joined on any more
 * (@see DocumentTemplateDoc.title), even though the endpoint contract names these three types by
 * their legacy code. The legacy codes are kept in the comments below as the only readable link
 * between the two catalogues.
 *
 * ⚠️ CONSEQUENCE TO KNOW: `title` is OPTIONAL until the migration that fills it in has run on the
 * types that predate it. A type still carrying none does not match here and the action does not
 * appear on it — deliberately, and with no fallback on `key`.
 *
 * @since 3.0.0
 */
export const FIRE_SAFETY_DISABLE_TITLES = [
  // PA19 - CPI presentazione SCIA ai VVF
  "firePreventionCPISCIASubmissionToTheFireBrigade",
  // PA20 - Asseverazione ai fini della prevenzione incendi
  "firePreventionFirePreventionSwornStatement",
  // PA30 - CPI certificato prevenzione incendi
  "firePreventionCPIFirePreventionCertificate",
];

/**
 * Answer of `PATCH /document-archive-docs/:docId/disable`.
 *
 * A REPORT and not an acknowledgement, in both of its fields:
 *  - `excluded: false` says the exclusion was ALREADY on record. The call did nothing, it did not
 *    fail, and telling the two apart is the difference between "done" and "it was already done";
 *  - `replacementDocId: null` is NOT an error and must never be reported as one. The back-end opens
 *    the periodic renewal behind the document that was switched off, and there are three legitimate
 *    cases where it opens none: PA19 on a category C fire safety element, a FREE / FREEBASIC plan
 *    (for PA19 and PA30 — on PA20 the replacement opens anyway, as in the legacy dossier), and an
 *    element that already carries a live replacement.
 *
 * RELOAD THE TREE afterwards in every case: the row that was switched off is deleted and disappears,
 * and a new empty mandatory row (PA31 or PA32) can have appeared on the same element.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocDisableResult {
  /** The row that was switched off */
  docId: string;
  /** `false` when the exclusion was already on record */
  excluded: boolean;
  /** The renewal opened behind it, `null` when none was — a legitimate answer */
  replacementDocId: string | null;
}

/**
 * Query accepted by GET /document-archive-docs.
 *
 * Every filter is applied by the BACK-END: pass them through instead of narrowing an
 * already downloaded list, so that what is rendered is what the back-end selected.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocListParams {
  /** MANDATORY: there is no global listing of the collection */
  documentArchiveId: string;
  /** Filter by document type. Replacement of the legacy `key` filter, which does not exist. */
  documentTemplateDocId?: string;
  structureObjectId?: string;
  contract?: boolean;
  expired?: boolean;
  /** Defaults to false on the back-end */
  includeDeleted?: boolean;
  /** Defaults to false on the back-end */
  includeMockup?: boolean;
  skip?: number;
  /** Capped at 500 by the back-end */
  limit?: number;
}

/**
 * Payload of GET /document-archive-docs.
 *
 * Ordered by `{ documentTemplateDocId, createdAt }`: the copies of the same document
 * type are contiguous and the oldest comes first. The order BETWEEN groups is ObjectId
 * order, which is meaningless for rendering: the instances are meant to be placed under
 * the template tree, which is walked by `position`.
 *
 * WHICH ROWS COME BACK — this is not every row of the archive:
 *  - every MANDATORY document, present or not, because an unfulfilled obligation is the
 *    thing the dossier exists to show. The missing ones carry `present: false`;
 *  - the NON MANDATORY ones only when present.
 *
 * So an optional document never uploaded is absent from `items`, from `totalCount` and from
 * `counters`. The row exists in the database — the taxonomy explodes one per expected
 * document type — but it is not a document of the archive. A screen that wants to offer the
 * empty optional slots (to pick which one to upload, say) cannot get them from here, nor from
 * `/tree`, which prunes them: the only source is the template tree, which asks for
 * `canCreateSettings`, so a role without it has no way to list them. No query flag exists for it
 * (confirmed by the back-end on 2026-10-08).
 *
 * @since 3.0.0
 */
export interface DocumentArchiveDocList {
  items: DocumentArchiveDocListItem[];
  totalCount: number;
  /** Always there, zeroed on an empty result: never null, never absent */
  counters: DocumentArchiveDocCounters;
}

/**
 * The band a document belongs to, in the order they are rendered.
 *
 * `expired` OVERRIDES everything: a mandatory document that is uploaded but expired goes to
 * the bottom, not among the fulfilled ones. Inside `expired` mandatory and optional are mixed
 * and ordered by `position` alone.
 *
 * CLOSED vocabulary, and NOT the same thing as the counters, despite three shared names:
 * @see DocumentArchiveDocCounters
 *
 * @since 3.0.0
 */
export type DocumentArchiveSortGroup =
  | "mandatoryPresent"
  | "mandatoryMissing"
  | "optionalPresent"
  | "expired";

/**
 * Level 4 of the archive tree: a document TYPE with the documents the archive holds for it,
 * for ONE band, inside ONE group.
 *
 * Same fields as the template row (@see DocumentTemplateDoc) plus `sortGroup` and
 * `archiveDocs`.
 *
 * ⚠️ THE SAME DOCUMENT TYPE CAN APPEAR TWICE inside a group, once per band, each time holding
 * only the rows of that band. So `_id` alone is NOT unique: the identity of an entry is
 * `_id` + `sortGroup`. Never write a `trackBy` on `_id` alone — the default identity tracking
 * of `*ngFor` handles the repetition correctly.
 *
 * It is not an edge case, it is the normal state of every expired mandatory document: when one
 * expires, the back-end marks the old row expired AND creates an empty row for the new
 * obligation. So "Codice fiscale" arrives twice, once under `mandatoryMissing` with the empty
 * row and once under `expired` with the uploaded one, in their respective bands and therefore
 * NOT adjacent.
 *
 * Rows are SPLIT across the bands, never duplicated: no document is served twice, and the
 * counters count each row once.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeDoc extends DocumentTemplateDoc {
  /** The band of this entry. Its rows all belong to it. */
  sortGroup: DocumentArchiveSortGroup;
  /**
   * The documents of this type IN THIS BAND, oldest first (`createdAt`). Never empty: an entry
   * with no document is pruned by the back-end.
   *
   * More than one is normal — successive interventions, one copy per structure element, two
   * valid PDFs of the same type — and copies sharing a band stay together in one entry.
   *
   * CAVEAT `mandatory` exists both here on the type and on every row, and the two can
   * DIVERGE: the type carries the general rule, the row carries what applies to that single
   * document (an activation sometimes forces a type the template calls optional). Use the
   * ROW one to show the state of a document — bands and counters are computed on that one.
   *
   * Mockups (the virtual documents) are here only when the tree is asked with `includeMockup`, and
   * only for admin, operator and the guests (NEW-M44; the dossier asks, FE-M5): filed, and never
   * counted. Soft deleted rows are, but only the ones the cascade deleted along with a removed
   * mapping element, and only inside its hidden block
   * (@see DocumentArchiveDoc.deleted) — so an entry of a hidden block can hold rows that carry
   * `deleted: true`, and they are the only place in the tree where that happens.
   *
   * ⚠️ ACROSS THE TREE a row is not unique either: a `contract: true` row is served both under its
   * own area and inside the contracts section (@see DocumentArchiveTreeContent), as the SAME row
   * with the SAME `_id`. So a list key must carry the path (area, section, group, band) and a
   * write done from one of the two views must reload the whole tree, not one section.
   */
  archiveDocs: DocumentArchiveTreeRow[];
}

/**
 * What a node stands for.
 *
 * On a GROUP it says where the grouping comes from, and `null` marks the container the back-end
 * invents to give one shape to every section: that one prints no heading and takes no number.
 *
 * On a SECTION (@see DocumentArchiveTreeContent) it appears only as `"structureObject"`, to say
 * that the section level is occupied by a mapping element instead of by a node of the taxonomy —
 * the inverted nesting of the property units, where the unit is the container and the sections
 * drop one level down as `"templateSection"` blocks.
 *
 * @since 3.0.0
 */
export type DocumentArchiveGroupSource =
  | "conductor"
  | "intervention"
  | "supplier"
  | "structureObject"
  /**
   * A block that stands for a SECTION OF THE TAXONOMY, not for a value: the shape used under a
   * property unit, where the six sections of the unit sit at the block level.
   *
   * It exists to keep `source: null` meaning what it means — "container, no heading, no number" —
   * on both sides. These blocks do print a heading and do take a number, and their label is a
   * localized pair in `translation`, not a literal string.
   */
  | "templateSection";

/**
 * The curated list a section offers when archiving a document.
 *
 * It is NOT the groups: the groups are built on the value each document copied into its own
 * `data`, so a value can sit in this list with no group behind it (registered, nothing filed
 * against it yet).
 *
 * Values come as stored — unsorted, not deduplicated, not normalized — precisely because the
 * groups are built on the raw string: a normalized dropdown would offer values matching no
 * group.
 *
 * Unused by the read only dossier, and described here so that nobody re-derives it from the
 * documents: it belongs to the insert form.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeDropdown {
  /** The `data` key the chosen value has to be written into */
  dataField: string;
  /** The list on the archive the values come from */
  source: string;
  values: string[];
}

/**
 * The archive's two curated name lists, as `GET /document-archive-docs/name-lists` serves them
 * (EXT-20) to the «Gestione Nome Conduttore» and «Gestione Nome Archiviazione Intervento» dialogs.
 *
 * Fields of the ARCHIVE document, not of its documents: the very two the legacy
 * `GET document-archives/:id` carries under the same names. They come as stored — strings only,
 * untrimmed, in their stored case, empty entries included — because the dialogs send a name back
 * as the `from` of a rename or a delete and the back-end matches it exactly.
 *
 * The insert form gets the same lists another way, inside the tree (@see DocumentArchiveTreeDropdown).
 *
 * @since 3.0.0
 */
export interface DocumentArchiveNameLists {
  /** The tenants; the archivings that list one store it upper-cased */
  conductorName: string[];
  /** The archiving interventions */
  nameInterventionArchiving: string[];
  /**
   * The supplier names (`nomeFornitore`), kept with the field by the decision of 27 October 2026
   * (FE-M6). Optional: the back-end serves them only once it keeps the list, and no dialog manages
   * them. The insert page reads them (@see insertPageNameLists).
   */
  suppliersName?: string[];
  /** The years of storage (`annoDiArchiviazione`), on the same terms as the supplier names */
  yearsOfStorage?: string[];
}

/**
 * Level 3 of the archive tree: a block of documents inside a section, with its heading.
 *
 * EVERY section carries groups, the plain ones included — that is the whole point: the front
 * end must not know WHICH sections are grouped. A plain section arrives as a single group with
 * `key`, `label` and `source` all `null`, and the rendering is one path for everything:
 *
 *   for each section, for each group: if there is a heading print it, then print the documents.
 *
 * ⚠️ NEVER ASSUME `groups.length === 1`. It used to hold for all but five sections; since the
 * back-end explodes the sections the taxonomy declares repeated over a mapping element (lifts,
 * extinguishers, tow away zones, employees, meters, property units…), 59 sections have more than
 * one. The rule never changed — a section is a LIST of blocks — but the plural case is now the
 * majority.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeGroup {
  /**
   * Printable numbering of the block, e.g. "3.1.3". Computed by the back-end over the PRUNED
   * tree, so it must be printed as it comes and never recomputed from indexes. It is progressive
   * over the VISIBLE blocks only, so hiding one leaves no hole in the sequence.
   *
   * ABSENT (`undefined`) in two cases:
   *  - the single group of an ordinary section: that group is the container the back-end creates
   *    to give one shape to every section, not a block the user sees, and its documents belong
   *    directly under the section;
   *  - a block with `active: false`, which is out of the numbering because it is out of the page
   *    unless the user asks for it — and likewise EVERY block sitting under a hidden section
   *    (@see DocumentArchiveTreeContent.number), `"templateSection"` ones included.
   *
   * A block with no label is still numbered: `number: "3.1.3"` with `label: null` is the
   * legitimate case of the documents belonging to no conductor / intervention / supplier / mapping
   * element.
   *
   * So "does this group render as a heading" is `number || label`, not `number` alone: a hidden
   * block has a label and no number, and it still needs its heading when it is shown.
   */
  number?: string;
  /**
   * Identity of the group INSIDE its section, `null` on an unlabelled group.
   *
   * Use it for list keys (`${content._id}:${group.key}`) and to keep UI state across a reload. It
   * equals `label` for conductors, interventions and suppliers, and it is the ObjectId of the
   * mapping ELEMENT when `source === "structureObject"` — where the two must not be swapped, since
   * two elements can carry the same name.
   */
  key: string | null;
  /**
   * Heading to print, already resolved as a literal string — never compose one out of a `data`
   * key, out of the section title plus an index, or out of the mapping. `null` means there is
   * nothing to print, and the documents render without a heading.
   *
   * For a mapping element it is the name as it reads in the mapping tab, PROGRESSIVE NUMBER
   * INCLUDED ("Passo carrabile 1"), already "Name Surname" for an employee, and an EMPTY STRING
   * when the element does not exist any more.
   */
  label: string | null;
  /**
   * Localized heading, served INSTEAD of `label` when the block stands for a node of the taxonomy
   * rather than for a value — the shape the property units will use, where the unit takes the
   * section level and the sections drop to the block level.
   *
   * Read it first and fall back on `label`: that is what keeps `/tree` free of the request
   * language, which it deliberately does not receive.
   */
  translation?: DocumentTemplateTranslation;
  /** Where the grouping comes from; `null` on a section that is not grouped */
  source: DocumentArchiveGroupSource | null;
  /**
   * Only on a `"templateSection"` block: the taxonomy identifier of the section that dropped to
   * this level (e.g. `"electricSystem"`).
   *
   * Additive and NOT for the user — the heading is `translation`. It is here because it is the only
   * thing left that says which section of the taxonomy the block used to be.
   */
  title?: string;
  /**
   * Only on the blocks of a section repeated over a mapping element
   * (`source === "structureObject"`).
   *
   * `false` = the element was DISABLED in the mapping, or REMOVED from it: the back-end does not
   * tell the two apart, and neither did the legacy dossier — a switched off element and a dangling
   * reference are the same thing to the reader. Such a block is HIDDEN by default and shown, in
   * red and flagged as deleted, only when the user asks for the deleted structures. Its documents
   * are already in the response, so that toggle costs no call.
   *
   * ⚠️ NOT the same rule as `structureActive` on a row (@see DocumentArchiveTreeRow), where a
   * dangling reference is `true`. Different questions: a row asks "is what this document refers to
   * in service", a block asks "does this block belong on the page at all".
   *
   * Absent on every other block, so the test is `=== false`.
   */
  active?: boolean;
  /**
   * The counts of THIS block, always complete — the ones of a hidden block included.
   *
   * ⚠️ They do NOT add up to the counters of the section: those exclude the rows of the hidden
   * blocks, on purpose, because a badge counts what the reader sees. Summing the blocks to
   * "correct" a section badge reintroduces exactly what the back-end took out.
   */
  counters: DocumentArchiveDocCounters;
  documents: DocumentArchiveTreeDoc[];
}

/**
 * Level 2 of the archive tree: a section, with the counts of its whole branch.
 *
 * ⚠️ There is NO `documents` here: the documents live in `groups[].documents`.
 *
 * THE CONTRACTS SECTION IS AGGREGATED. `title: "supplyAndServiceContracts"` does not hold only its
 * own documents: the back-end adds to it every `contract: true` row archived in any other area
 * (except the tenancy contracts section, which has an area of its own, and the document type keyed
 * `RF4`). It arrives assembled, in the position the template gives it, as a flat table: one group
 * with `key`, `label` and `source` all `null` and no `number`. Three consequences:
 *  - the rows it borrows are in the tree TWICE, same `_id` (@see DocumentArchiveTreeRow);
 *  - its `counters` — and the ones of its area and of the root — count only the documents NATIVE to
 *    the contracts area: a badge reading 10 above 110 rows is CORRECT, and it is what keeps the root
 *    totals equal to the ones of the flat listing. A screen needing the number of rows shown has to
 *    count them itself and present it as a different number, never in place of the badge;
 *  - only there do the rows carry the four resolved fields.
 *
 * Never rebuild the aggregate by scanning the other areas for `contract: true`: that would be a
 * second implementation of the exclusions and of the visibility rules.
 *
 * ITS ABSENCE IS NOT AN ERROR: a `temporary` or `shared` role without the `contratti` property
 * permission gets no such section at all (and no contracts area, if it held nothing else), with no
 * flag saying so. Nothing is to be hidden here, and the permission must not be inferred from the
 * absence to decide anything else — the contracts archived in other areas stay visible there.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeContent extends DocumentTemplateContent {
  /**
   * DOM anchor of the section, and the only thing this level is identified by here.
   *
   * ⚠️ NOT always an ObjectId: the node collecting the rows that name no mapping element carries a
   * SYNTHETIC id (`"<areaId>-unassigned"`). Stable across reloads, fine as an anchor, and never to
   * be validated as an ObjectId nor put into a route.
   */
  _id: string;
  /**
   * Printable numbering, e.g. "3.1". @see DocumentArchiveTreeGroup.number
   *
   * ONE RULE, on both levels: a node the reader does not see by default takes no number and
   * consumes no position, so the visible sequence never has a hole. THREE ways in for a section:
   *  1. it IS a disabled mapping element (`source === "structureObject"` with `active: false`), the
   *     property unit case;
   *  2. every one of its blocks is;
   *  3. it is a feature the property switched off (`active: false` with no marker), the employees
   *     section without active employees.
   * A section with only SOME blocks hidden keeps its number: it is on the page, minus those blocks.
   *
   * The rule runs DOWN the branch: under a hidden node neither the node nor its blocks are numbered,
   * so no number is ever built on a position a visible sibling occupies. Those headings stand on
   * their label alone, which is why a block must never be printed on `number` alone.
   */
  number: string;
  /**
   * What this section level stands for. ABSENT on an ordinary section — the normal case, where the
   * level belongs to the taxonomy.
   *
   * `"structureObject"` means the level is occupied by a MAPPING ELEMENT: the inverted nesting of
   * the property units, where the unit is the container and the taxonomy sections drop to the block
   * level. It says that `structureKey` is meaningful here, and it is the marker that tells apart the
   * two kinds of `active: false` (see below) — for what the counters and the obligations do, NOT for
   * visibility, which is the same for both.
   *
   * ⚠️ It is also carried by the node collecting the rows that name NO element, which has the marker
   * but neither `structureKey` nor `translation`, and `active: true`. Its heading is therefore the
   * bare number. Those rows are MORE than the legacy showed — it walked the elements and never met
   * them, so an unfulfilled obligation was simply disappearing.
   */
  source?: DocumentArchiveGroupSource | null;
  /**
   * Identity of the mapping element when `source === "structureObject"`, absent otherwise. Same
   * role as `DocumentArchiveTreeGroup.key`: list keys and UI state across a reload.
   *
   * NOT named `key` on purpose, even though the block level calls it that: at THIS level `key` is
   * already taken by the template (@see DocumentTemplateContent), where it is the legacy join key
   * of the section. Two meanings on one name, which is the very thing `active` was kept away from.
   *
   * ⚠️ Do NOT use it as the DOM anchor of the section: that is `_id`, which stays populated at this
   * level whatever the node stands for.
   */
  structureKey?: string;
  /**
   * The counts of the whole branch, MINUS the rows of the blocks hidden as disabled structures
   * (@see DocumentArchiveTreeGroup.active). Same for the area and the root.
   *
   * A section whose blocks are ALL hidden still arrives — its rows have to stay reachable — with
   * every count at zero.
   */
  counters: DocumentArchiveDocCounters;
  /**
   * The blocks of the section. Always at least one, usually more (@see
   * DocumentArchiveTreeGroup).
   *
   * Ordered by the back-end, and the order DEPENDS ON `source`: alphabetically by `label` for
   * conductors, interventions and suppliers, in MAPPING ORDER for the blocks of a mapping element
   * — which is the order the mapping tab shows, not an alphabetical one. The unlabelled block is
   * always LAST. Nothing here is to be re-sorted.
   *
   * ONE EXCEPTION, the fire prevention section: it is the only one whose blocks are elements of
   * MIXED TYPES, and there they come grouped by type in a fixed order (dwellings, garage
   * compartments, boiler rooms, generator sets, fire prevention procedures), with the mapping order
   * applying only inside a type. It looks out of order next to the other repeated sections, and it
   * is not: it is what the legacy dossier shows.
   */
  groups: DocumentArchiveTreeGroup[];
  /** Only on the sections that offer a curated list at insert time */
  dropdown?: DocumentArchiveTreeDropdown;
  /**
   * `false` = this section is not in service. WHAT is out of service depends on `source`, and the
   * two cases share only what the reader sees:
   *
   *  - HIDDEN by default and revealed, in red, by the "show deleted structures" toggle;
   *  - no `number`, hence out of the sequence (@see number).
   *
   * They diverge on everything the back-end computes, so never read one rule for the other:
   *
   *  - `source` absent — a FEATURE the property switched off, today the employees section. Its
   *    documents are NOT obligations (served with `mandatory: false`, the ones never uploaded not
   *    served at all) and its rows ARE counted in the counters of the area and of the root;
   *  - `source === "structureObject"` — a MAPPING ELEMENT disabled or gone, exactly
   *    @see DocumentArchiveTreeGroup.active one level up. Obligations are NOT relaxed (switching an
   *    element off does not make its documents optional) and its rows are OUT of those counters.
   *
   * Either way the counters are to be printed as they arrive: the difference is already in them.
   *
   * Every other section does not carry the field at all — `undefined`, not `true` — so test it with
   * `=== false`.
   */
  active?: boolean;
}

/**
 * Level 1 of the archive tree: an area, with the counts of its whole branch.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTreeArea extends DocumentTemplateArea {
  /**
   * Printable numbering, e.g. "3".
   *
   * Computed over the PRUNED tree: "3" is the third area RECEIVED, not the third of the taxonomy,
   * because the areas holding no document were removed and consume no number. Print it as it
   * comes — deriving it from the loop index would drift as soon as a branch is pruned.
   */
  number: string;
  counters: DocumentArchiveDocCounters;
  content: DocumentArchiveTreeContent[];
}

/**
 * Payload of GET /document-archive-docs/tree: the archive of one property, already
 * assembled and ready to render.
 *
 * It replaces the pair `GET /document-template/tree` + `GET /document-archive-docs` and the
 * join between them. It also replaces them for a second reason: the template tree is gated
 * behind `canCreateSettings`, the permission to EDIT the taxonomy, while this endpoint asks
 * for `canReadDocumentsList` — the right of someone who only has to read the documents.
 *
 * WHAT IS IN THE TREE: every mandatory document, uploaded or not, and the non mandatory ones
 * only when uploaded.
 *
 * ON A FREE / FREEBASIC PROPERTY every `contract: true` row arrives with `mandatory: false`, in the
 * WHOLE tree and not only in the contracts section, because a contract is not an obligation on the
 * free plan. The ones never uploaded arrive all the same, so that they can be. Consequence: on
 * those properties the counters of this tree do NOT match the ones of the flat listing, which
 * counts the stored value and knows nothing about the plan — a screen comparing the two numbers
 * has to stop comparing them.
 *
 * WHAT IS PRUNED, bottom up: an entry with no document, then a section left with no entry,
 * then an area left with no section. So a branch that does not arrive is NOT an error and NOT
 * something to render a placeholder for: it holds no document.
 *
 * ALREADY ORDERED and ALREADY GROUPED, at every level: areas and sections by `position`, the
 * groups of a section by `label` with the unlabelled one last, the entries of a group by band
 * and then by `position`, the rows of an entry by `createdAt`. Nothing is to be re-sorted nor
 * re-grouped here.
 *
 * The bands live INSIDE the groups: every conductor has its own expired ones at the bottom of
 * its own block, there is no global expired band.
 *
 * Three absences that are invisible from here, by design:
 *  - mockups, unless asked with `includeMockup` — then admin, operator and the guests get them,
 *    filed and never counted (NEW-M44, FE-M5) —, and the rows deleted BY HAND. The ones the
 *    cascade deleted with a removed mapping element DO arrive, inside its hidden block
 *    (@see DocumentArchiveDoc.deleted) — with one exception: the aggregated contracts table never
 *    holds them, because it is flat and has no block for the toggle to hide, so a deleted contract
 *    would sit among the live ones. It stays reachable in its own area;
 *  - a type the taxonomy gained after the property was activated, until its row is created:
 *    the rows come from events on the property, not from edits to the template;
 *  - a document whose type was RETIRED from the template, even if uploaded. It stays
 *    reachable through the flat listing, never through this tree.
 *
 * It takes no filter but `includeMockup`, and there is no pagination on purpose: a page of a tree
 * is not a tree, and the counters of a node describe its whole branch.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveTree {
  /** Always resolved, even when the tree was asked for by `propertyId` */
  documentArchiveId: string;
  propertyId: string;
  /** Documents placed in the tree */
  totalCount: number;
  /** Counts of the whole archive. Every level is the sum of its children. */
  counters: DocumentArchiveDocCounters;
  area: DocumentArchiveTreeArea[];
}
